import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface SettingsViewProps {
  // Add props as needed
}

interface MetaApiSettings {
  autoDeploy: boolean;
}

const SettingsView: React.FC<SettingsViewProps> = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [metaApiSettings, setMetaApiSettings] = useState<MetaApiSettings>({
    autoDeploy: true
  });

  // Redirect if not technical admin
  React.useEffect(() => {
    if (!userData?.isTechnicalAdmin) {
      navigate('/admin');
    }
  }, [userData, navigate]);

  // Load settings on component mount
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'metaApi'));
        if (settingsDoc.exists()) {
          setMetaApiSettings(settingsDoc.data() as MetaApiSettings);
        }
      } catch (error) {
        console.error('Error loading MetaAPI settings:', error);
      }
    };
    loadSettings();
  }, []);

  const handleSaveSettings = async () => {
    try {
      setSaveStatus('saving');
      const settingsRef = doc(db, 'settings', 'metaApi');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        // Update existing document
        await updateDoc(settingsRef, {
          autoDeploy: metaApiSettings.autoDeploy
        });
      } else {
        // Create new document
        await setDoc(settingsRef, {
          autoDeploy: metaApiSettings.autoDeploy
        });
      }
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving MetaAPI settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Technical Settings</h1>
          <Link
            to="/admin"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Back to Admin Dashboard
          </Link>
        </div>
        
        <div className="space-y-6">
          {/* MetaAPI Deployment Settings */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold mb-4">MetaAPI Deployment Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={metaApiSettings.autoDeploy}
                    onChange={(e) => setMetaApiSettings(prev => ({ ...prev, autoDeploy: e.target.checked }))}
                    className="form-checkbox h-5 w-5 text-indigo-600"
                  />
                  <span>Auto-deploy new users on MetaAPI</span>
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleSaveSettings}
                  disabled={saveStatus === 'saving'}
                  className={`px-4 py-2 rounded-md text-white ${
                    saveStatus === 'saving' 
                      ? 'bg-gray-400' 
                      : saveStatus === 'success'
                      ? 'bg-green-600'
                      : saveStatus === 'error'
                      ? 'bg-red-600'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                >
                  {saveStatus === 'saving' 
                    ? 'Saving...' 
                    : saveStatus === 'success'
                    ? 'Saved!'
                    : saveStatus === 'error'
                    ? 'Error!'
                    : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>

          {/* Other settings sections */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-4">System Configuration</h2>
            <p className="text-gray-600">System-wide settings and configurations will be managed here.</p>
          </div>

          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-4">API Settings</h2>
            <p className="text-gray-600">API configurations and integrations will be managed here.</p>
          </div>

          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-4">Security Settings</h2>
            <p className="text-gray-600">Security and access control settings will be managed here.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView; 